import { useEffect } from 'react';
import { Box, Button, Container, HStack, Heading, Text, VStack } from '@chakra-ui/react';
import { PageWrapper } from '@/components/layout';
import { SEO } from '@/components/seo';
import AppHeader from '@/components/navigation/AppHeader';
import { ArrowLeft, Moon, Smartphone, Sun } from 'lucide-react';
import { useRouter } from '@tanstack/react-router';
import { useAppearance } from '@/lib/appearance/useAppearance';
import type { AppearanceIntent } from '@/lib/appearance/constants';
import { markAppearanceBadgeSeen } from '@/lib/appearance/badgeSeen';

/**
 * D45a — member-facing Light/Dark/System appearance settings row (FR-T2 UI).
 * Live for all members since Phase 7 (see Menu.tsx's entry point).
 */
const OPTIONS: { intent: AppearanceIntent; label: string; icon: typeof Sun }[] = [
  { intent: 'light', label: 'Light', icon: Sun },
  { intent: 'dark', label: 'Dark', icon: Moon },
  { intent: 'system', label: 'System', icon: Smartphone },
];

export function AppearanceSettings() {
  const router = useRouter();
  const { intent, resolved, setIntent } = useAppearance();

  useEffect(() => {
    markAppearanceBadgeSeen();
  }, []);

  return (
    <PageWrapper>
      <SEO title="Appearance Settings" />
      <AppHeader page={'settings'} />
      <Box minH="100vh">
        <Container maxW="container.md" py={6}>
          <VStack spacing={6} align="stretch">
            <HStack justify="space-between" align="center">
              <Button
                leftIcon={<ArrowLeft size={18} />}
                variant="ghost"
                onClick={() => router.history.back()}
                size={{ base: 'sm', lg: 'md' }}
                color="var(--canvas-text)"
                _hover={{ bg: 'var(--color-white-overlay-10)' }}
                _active={{ bg: 'var(--color-white-overlay-20)' }}
              >
                Back
              </Button>
              <Heading size="lg" color="var(--canvas-text)">
                Appearance
              </Heading>
              <Box w="60px" />
            </HStack>

            <Box
              p={6}
              borderRadius="var(--r-card)"
              bg="var(--card-bg)"
              border="var(--card-border)"
              boxShadow="var(--card-shadow)"
            >
              <Heading size="md" mb={1} color="var(--card-text)">
                Light / Dark / System
              </Heading>
              <Text fontSize="sm" color="var(--card-text-2)" mb={4}>
                System follows your device setting and updates automatically. Currently resolved to{' '}
                {resolved}.
              </Text>
              <HStack spacing={3}>
                {OPTIONS.map(({ intent: optionIntent, label, icon: Icon }) => {
                  const isSelected = intent === optionIntent;
                  return (
                    <Button
                      key={optionIntent}
                      onClick={() => setIntent(optionIntent)}
                      leftIcon={<Icon size={18} />}
                      variant={isSelected ? 'solid' : 'outline'}
                      bg={isSelected ? 'purple.500' : 'transparent'}
                      color={isSelected ? 'white' : 'var(--card-text)'}
                      _hover={{ bg: isSelected ? 'purple.600' : 'var(--card-surface-2)' }}
                      flex={1}
                      aria-pressed={isSelected}
                    >
                      {label}
                    </Button>
                  );
                })}
              </HStack>
            </Box>
          </VStack>
        </Container>
      </Box>
    </PageWrapper>
  );
}
